import { useState, useEffect, useCallback } from 'react';
import { fetchPersonalLoans, createPersonalLoan, updatePersonalLoan, deletePersonalLoan } from '@/lib/api';
import { formatMXN, formatDate } from '@/lib/formatters';
import { PageHeader } from '@/components/common/PageHeader';
import { EmptyState } from '@/components/common/EmptyState';
import { KPICard } from '@/components/common/KPICard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  Plus, Trash2, Landmark, Pencil, Check, X as XIcon,
  DollarSign, Calendar, Clock, TrendingDown,
} from 'lucide-react';

const emptyForm = { lender_name: '', monthly_payment: '', start_month: '', end_month: '' };

/* ═══════════════════════════════════════════════════════
   Main Préstamos Page
   ═══════════════════════════════════════════════════════ */
export default function Prestamos() {
  const [loans, setLoans]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const load = useCallback(async () => {
    try { setLoans(await fetchPersonalLoans()); }
    catch { toast.error('Error al cargar préstamos'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    try { await deletePersonalLoan(id); setLoans(ls => ls.filter(l => l.id !== id)); toast.success('Préstamo eliminado'); }
    catch { toast.error('Error al eliminar'); }
  };
  const handleCreated = (c) => { setLoans(ls => [...ls, c]); setCreateOpen(false); };
  const handleUpdated = (u) => { setLoans(ls => ls.map(l => l.id === u.id ? u : l)); setEditTarget(null); };

  /* ── Aggregates ── */
  const totalMensual  = loans.reduce((s, l) => s + (l.monthly_payment || 0), 0);
  const activosMensual = loans.filter(l => l.activo).reduce((s, l) => s + l.monthly_payment, 0);
  const totalCompromiso = loans.reduce((s, l) => s + (l.monto_total || 0), 0);
  const activeCount   = loans.filter(l => l.activo).length;

  /* ── Timeline range for visual ── */
  const allMonths = loans.flatMap(l => [l.start_month, l.end_month]).filter(Boolean).sort();
  const minMonth = allMonths[0] || '2024-01';
  const maxMonth = allMonths[allMonths.length - 1] || '2027-12';

  const monthToOffset = (m) => {
    if (!m) return 0;
    const [sy, sm] = minMonth.split('-').map(Number);
    const [y, mo]  = m.split('-').map(Number);
    return (y - sy) * 12 + (mo - sm);
  };
  const totalSpan = monthToOffset(maxMonth) + 1;

  if (loading) return (
    <div className="space-y-6">
      <Skeleton className="h-10 w-48" />
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
      <Skeleton className="h-[300px] rounded-xl" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="prestamos-page">
      <PageHeader title="Préstamos Personales" subtitle={`${loans.length} préstamos registrados`}>
        <Button onClick={() => setCreateOpen(true)} data-testid="prestamos-add-button">
          <Plus className="h-4 w-4 mr-1.5" /> Nuevo Préstamo
        </Button>
      </PageHeader>

      {/* ── KPIs ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KPICard label="Pago Mensual Total" value={formatMXN(totalMensual)} icon={DollarSign} subtitle={`${loans.length} préstamos`} compact testId="kpi-prestamos-total" />
        <KPICard label="Activos Ahora" value={formatMXN(activosMensual)} icon={Clock} subtitle={`${activeCount} activos`} compact testId="kpi-prestamos-activos" />
        <KPICard label="Compromiso Total" value={formatMXN(totalCompromiso)} icon={TrendingDown} subtitle="Vida de préstamos" compact testId="kpi-prestamos-compromiso" />
        <KPICard label="Préstamos" value={`${activeCount} activos de ${loans.length}`} icon={Landmark} compact testId="kpi-prestamos-count" />
      </div>

      {/* ── Timeline Visual ── */}
      {loans.length > 0 && (
        <Card className="rounded-xl border bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-[#F05A28]" /> Línea de Tiempo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3" data-testid="prestamos-timeline">
              {loans.map(l => {
                const startOff = monthToOffset(l.start_month);
                const endOff   = monthToOffset(l.end_month);
                const width    = Math.max(((endOff - startOff + 1) / totalSpan) * 100, 2);
                const left     = (startOff / totalSpan) * 100;
                return (
                  <div key={l.id} className="flex items-center gap-3">
                    <div className="w-36 shrink-0 text-right">
                      <p className="text-xs font-medium text-slate-700 truncate">{l.lender_name}</p>
                      <p className="text-[10px] text-slate-400 tabular-nums">{formatMXN(l.monthly_payment)}/mes</p>
                    </div>
                    <div className="flex-1 relative h-7 bg-slate-100 rounded-md overflow-hidden">
                      <div
                        className={`absolute inset-y-0 rounded-md transition-all ${l.activo ? 'bg-gradient-to-r from-[#F05A28] to-[#FF6A3D]' : 'bg-slate-300'}`}
                        style={{ left: `${left}%`, width: `${width}%` }}
                      >
                        <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white drop-shadow-sm whitespace-nowrap px-1">
                          {l.total_meses}m
                        </span>
                      </div>
                    </div>
                    <div className="w-28 shrink-0">
                      <p className="text-[10px] text-slate-400">{l.start_label} → {l.end_label}</p>
                    </div>
                  </div>
                );
              })}
              {/* Axis labels */}
              <div className="flex items-center gap-3 mt-1">
                <div className="w-36 shrink-0" />
                <div className="flex-1 flex justify-between text-[9px] text-slate-300 tabular-nums">
                  <span>{minMonth}</span>
                  <span>{maxMonth}</span>
                </div>
                <div className="w-28 shrink-0" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Table ── */}
      {loans.length === 0 ? <EmptyState title="Sin préstamos" description="Agrega el primer préstamo para comenzar." icon={Landmark} /> : (
        <Card className="rounded-xl border bg-white overflow-hidden">
          <div className="overflow-auto">
            <Table data-testid="prestamos-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  {['Acreedor','Pago Mensual','Inicio','Fin','Meses','Monto Total','Estado',''].map(h => (
                    <TableHead key={h} className={`text-xs uppercase tracking-wide text-slate-600 font-semibold py-3 px-3 whitespace-nowrap ${['Pago Mensual','Meses','Monto Total'].includes(h) ? 'text-right' : ''}`}>{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loans.map((l, idx) => (
                  <TableRow key={l.id} className="table-row-hover group" data-testid={`prestamos-row-${idx}`}>
                    <TableCell className="py-2.5 px-3 font-medium text-sm">{l.lender_name}</TableCell>
                    <TableCell className="py-2.5 px-3 text-right tabular-nums text-sm font-medium">{formatMXN(l.monthly_payment)}</TableCell>
                    <TableCell className="py-2.5 px-3 text-sm text-slate-500">{l.start_label}</TableCell>
                    <TableCell className="py-2.5 px-3 text-sm text-slate-500">{l.end_label}</TableCell>
                    <TableCell className="py-2.5 px-3 text-right tabular-nums text-sm">{l.total_meses}</TableCell>
                    <TableCell className="py-2.5 px-3 text-right tabular-nums text-sm">{formatMXN(l.monto_total)}</TableCell>
                    <TableCell className="py-2.5 px-3">
                      {l.activo ? (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">Activo</Badge>
                      ) : (
                        <Badge variant="outline" className="bg-slate-50 text-slate-500 border-slate-200 text-xs">Inactivo</Badge>
                      )}
                    </TableCell>
                    <TableCell className="py-2.5 px-3">
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-slate-700" onClick={() => setEditTarget(l)} data-testid={`prestamos-edit-${idx}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-500" onClick={() => handleDelete(l.id)} data-testid={`prestamos-delete-${idx}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {/* Totals */}
                <TableRow className="bg-slate-50 font-semibold">
                  <TableCell className="py-2.5 px-3 text-sm">Total</TableCell>
                  <TableCell className="py-2.5 px-3 text-right tabular-nums text-sm">{formatMXN(totalMensual)}</TableCell>
                  <TableCell colSpan={3} />
                  <TableCell className="py-2.5 px-3 text-right tabular-nums text-sm">{formatMXN(totalCompromiso)}</TableCell>
                  <TableCell colSpan={2} />
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* ── Helper: Monthly Logic Explanation ── */}
      {loans.length > 0 && (
        <Card className="rounded-xl border bg-white">
          <CardContent className="p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Lógica de Cálculo Mensual</p>
            <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600 space-y-1 font-mono">
              <p>si mes_actual ≥ inicio <span className="text-slate-400">Y</span> mes_actual ≤ fin → <span className="text-emerald-600 font-bold">incluir pago</span></p>
              <p className="text-slate-400">// Para cada mes del flujo de caja, se suman los pagos de préstamos activos en ese período</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Modals ── */}
      <LoanModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={handleCreated}
        title="Nuevo Préstamo"
      />
      {editTarget && (
        <LoanModal
          open={true}
          onClose={() => setEditTarget(null)}
          onSaved={handleUpdated}
          title={`Editar — ${editTarget.lender_name}`}
          initial={editTarget}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   Unified Create / Edit Modal
   ═══════════════════════════════════════════════════════ */
function LoanModal({ open, onClose, onSaved, title, initial }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    lender_name: initial?.lender_name || '',
    monthly_payment: initial?.monthly_payment || '',
    start_month: initial?.start_month || '',
    end_month: initial?.end_month || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const d = { lender_name: form.lender_name, monthly_payment: parseFloat(form.monthly_payment) || 0, start_month: form.start_month, end_month: form.end_month };
      let result;
      if (isEdit) { result = await updatePersonalLoan(initial.id, d); }
      else { result = await createPersonalLoan(d); }
      onSaved(result);
      toast.success(isEdit ? 'Préstamo actualizado' : 'Préstamo creado');
    } catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  /* ── Preview calculations ── */
  const pago = parseFloat(form.monthly_payment) || 0;
  const meses = (() => {
    try {
      const [sy, sm] = form.start_month.split('-').map(Number);
      const [ey, em] = form.end_month.split('-').map(Number);
      return (ey - sy) * 12 + (em - sm) + 1;
    } catch { return 0; }
  })();
  const montoTotal = pago * Math.max(meses, 0);

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-[rgba(240,90,40,0.1)] flex items-center justify-center">
              <Landmark className="h-4 w-4 text-[#F05A28]" />
            </div>
            {title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">Acreedor / Nombre</label>
            <Input value={form.lender_name} onChange={e => set('lender_name', e.target.value)} placeholder="Nombre del acreedor" data-testid="loan-name" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">Pago Mensual (MXN)</label>
            <Input type="number" value={form.monthly_payment} onChange={e => set('monthly_payment', e.target.value)} placeholder="0" data-testid="loan-payment" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Mes Inicio</label>
              <Input type="month" value={form.start_month} onChange={e => set('start_month', e.target.value)} data-testid="loan-start" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Mes Fin</label>
              <Input type="month" value={form.end_month} onChange={e => set('end_month', e.target.value)} data-testid="loan-end" />
            </div>
          </div>

          {/* Preview */}
          {pago > 0 && meses > 0 && (
            <div className="bg-slate-50 rounded-lg p-3 space-y-1.5">
              <div className="flex justify-between text-xs">
                <span className="text-slate-500">Duración</span>
                <span className="font-medium tabular-nums">{meses} meses</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-500">Pago mensual</span>
                <span className="font-medium tabular-nums">{formatMXN(pago)}</span>
              </div>
              <Separator />
              <div className="flex justify-between text-sm">
                <span className="text-slate-700 font-medium">Monto total</span>
                <span className="font-bold tabular-nums">{formatMXN(montoTotal)}</span>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="outline">Cancelar</Button></DialogClose>
          <Button onClick={handleSave} disabled={!form.lender_name || !form.monthly_payment || !form.start_month || !form.end_month || saving} data-testid="loan-save">
            {saving ? 'Guardando...' : isEdit ? 'Guardar Cambios' : 'Crear Préstamo'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
